/**
 * /erm2026/del-palacio-al-concejo/
 * Pieza editorial cívico-electoral · neutral, no partidaria.
 * Análisis sobre la pregunta jurídico-electoral del "electo no incorporado"
 * en el marco de la calificación de listas ERM 2026.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";

const PDF_URL = "/erm2026/analisis/Del_Palacio_al_Concejo_ENSAYO_POLITICO_ELECTORAL_JFT_20260624.pdf";

export function DelPalacioAlConcejoPage() {
  // SEO · meta title + description al montar
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Del Palacio al Concejo: ¿cuándo empieza el mandato? | ERM 2026";

    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const desc =
      "Análisis cívico-electoral sobre el 'electo no incorporado': ¿la sola proclamación crea el mandato? Las dos tesis y por qué importa en las ERM 2026.";

    setMeta("description", desc);
    setMeta("og:title", "Del Palacio al Concejo: ¿cuándo empieza el mandato?", "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "article", "property");
    setMeta("og:url", "https://peru.verdadelectoral.com/erm2026/del-palacio-al-concejo/", "property");
    setMeta(
      "og:image",
      "https://peru.verdadelectoral.com/erm2026/analisis/del-palacio-al-concejo-og.jpg",
      "property"
    );
    setMeta("twitter:card", "summary_large_image");
    setMeta(
      "keywords",
      "ERM 2026, derecho electoral, mandato, proclamación, calificación de listas, participación política, electo no incorporado, JNE, JEE"
    );

    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="bg-white text-slate-900">
      {/* Breadcrumb */}
      <nav className="container-prose pt-6 text-xs text-slate-500" aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link to="/" className="hover:text-brand-navy">Inicio</Link>
          </li>
          <li aria-hidden>›</li>
          <li>
            <Link to="/erm2026" className="hover:text-brand-navy">ERM 2026</Link>
          </li>
          <li aria-hidden>›</li>
          <li>
            <span className="text-slate-700">Análisis</span>
          </li>
          <li aria-hidden>›</li>
          <li className="text-brand-navy font-semibold" aria-current="page">
            Del Palacio al Concejo
          </li>
        </ol>
      </nav>

      {/* Hero institucional · con portada del castillo */}
      <header className="border-b-4 border-brand-red bg-brand-navy-50 mt-2">
        <div className="container-prose py-10 md:py-14">
          <div className="grid gap-8 md:grid-cols-[1fr_280px] items-start">
            <div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-navy text-white px-2.5 py-1 font-semibold uppercase tracking-widest">
                  ERM 2026
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-white border border-brand-navy/30 text-brand-navy px-2.5 py-1 font-semibold uppercase tracking-widest">
                  Análisis jurídico-electoral
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-red text-white px-2.5 py-1 font-semibold uppercase tracking-widest">
                  Cívico · No partidario
                </span>
              </div>
              <h1 className="mt-5 font-serif text-4xl md:text-5xl text-brand-navy leading-tight">
                Del Palacio al Concejo:{" "}
                <span className="text-brand-red">¿cuándo empieza realmente el mandato?</span>
              </h1>
              <p className="mt-4 text-lg text-slate-700 leading-relaxed font-serif italic">
                La fragilidad del mandato no asumido. Un análisis sobre una de las preguntas más
                finas que dejó la calificación de listas en las Elecciones Regionales y
                Municipales 2026.
              </p>
              <div className="mt-6">
                <a
                  href={PDF_URL}
                  download
                  className="inline-flex items-center gap-2 rounded-md bg-brand-red text-white px-6 py-3 text-base font-bold hover:bg-brand-red-700 transition shadow-lg"
                >
                  📥 Descargar PDF
                </a>
                <p className="mt-2 text-xs text-slate-600">PDF · 36 páginas · 766 KB · Junio 2026</p>
              </div>
            </div>
            <div className="md:justify-self-end">
              <a
                href={PDF_URL}
                download
                aria-label="Descargar el ensayo en PDF"
                className="block rounded-md overflow-hidden shadow-2xl ring-1 ring-brand-navy/20 hover:ring-brand-red/60 transition"
              >
                <img
                  src="/erm2026/analisis/imagenes/portada_castillo.png"
                  alt="Portada del ensayo: Del Palacio al Concejo · La fragilidad del mandato no asumido"
                  className="w-full h-auto"
                  loading="eager"
                />
              </a>
              <p className="mt-2 text-xs text-center text-brand-red font-semibold">
                Click sobre la portada para descargar el PDF
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container-prose py-12">
        {/* Entradilla · resumen 3 líneas + planteamiento */}
        <section className="max-w-3xl">
          <p className="text-lg text-slate-800 leading-relaxed">
            Ganar una elección no es lo mismo que ejercer el poder. Entre ser{" "}
            <em>proclamado electo</em> y convertirse en autoridad hay una secuencia de actos —
            proclamación, credencial, juramento, incorporación y ejercicio — que solemos confundir
            en uno solo.
          </p>
          <p className="mt-4 text-lg text-slate-800 leading-relaxed">
            ¿La sola proclamación basta para que exista un mandato, o el mandato nace recién
            cuando la persona se incorpora y ejerce el cargo? De cómo se responda esa pregunta
            depende algo muy concreto:{" "}
            <strong className="text-brand-navy">
              si un ciudadano puede o no participar en un nuevo proceso electoral
            </strong>
            .
          </p>
        </section>

        {/* El problema en simple */}
        <section className="mt-12 max-w-3xl">
          <h2 className="font-serif text-2xl text-brand-navy">El problema, en simple</h2>
          <p className="mt-3 text-base text-slate-700 leading-relaxed">
            La Constitución y la ley regulan con claridad la situación de las autoridades en
            ejercicio. Pero existe una zona menos explorada: la del ciudadano que fue{" "}
            <em>proclamado electo</em> y todavía no juramentó ni asumió funciones.{" "}
            <strong className="text-brand-navy">
              ¿Debe tratarse igual que a una autoridad en ejercicio?
            </strong>
          </p>
          <p className="mt-3 text-base text-slate-700 leading-relaxed">
            Esta es la pregunta que el ensayo ordena y analiza, sin consignas.
          </p>
        </section>

        {/* Las dos tesis · lado a lado */}
        <section className="mt-12">
          <h2 className="font-serif text-2xl text-brand-navy">Las dos interpretaciones en juego</h2>
          <p className="mt-2 text-sm text-slate-600">
            El ensayo desarrolla ambas con su mejor argumento y cierra con los criterios
            constitucionales para ponderarlas.
          </p>

          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {/* Tesis 1 · favor participationis */}
            <article className="rounded-lg border-2 border-brand-navy/30 bg-white p-6 shadow-sm">
              <div className="text-xs uppercase tracking-widest text-brand-navy font-bold">
                Tesis A
              </div>
              <h3 className="mt-1 font-serif text-xl text-brand-navy">
                Favorable a la participación
              </h3>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                La proclamación reconoce un resultado, pero el mandato funcional nace con la{" "}
                <strong>incorporación</strong> y el <strong>ejercicio</strong>. Si no hubo
                juramento ni función, no hay impedimento expreso y debe primar el derecho a
                participar.
              </p>
              <p className="mt-3 text-xs text-slate-500 font-mono">
                Principio rector: <em>favor participationis</em>
              </p>
            </article>

            {/* Tesis 2 · restrictiva */}
            <article className="rounded-lg border-2 border-brand-red/30 bg-white p-6 shadow-sm">
              <div className="text-xs uppercase tracking-widest text-brand-red font-bold">
                Tesis B
              </div>
              <h3 className="mt-1 font-serif text-xl text-brand-red">Restrictiva</h3>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                El mandato y el vínculo representativo nacen con la{" "}
                <strong>voluntad popular</strong> expresada en las urnas y reconocida en la
                proclamación; por ello prevalecen la <strong>estabilidad institucional</strong> y
                el <strong>respeto al voto previo</strong>.
              </p>
              <p className="mt-3 text-xs text-slate-500 font-mono">
                Principio rector: estabilidad y representación
              </p>
            </article>
          </div>
        </section>

        {/* Por qué importa ERM 2026 */}
        <section className="mt-12 rounded-lg border-l-4 border-brand-red bg-brand-red-50 p-6 max-w-3xl">
          <h2 className="font-serif text-xl text-brand-navy">Por qué importa en las ERM 2026</h2>
          <p className="mt-3 text-base text-slate-800 leading-relaxed">
            La proximidad entre las Elecciones Generales y las Regionales y Municipales de 2026
            hizo que esta pregunta dejara de ser teórica y apareciera en la calificación de varias
            listas.{" "}
            <strong className="text-brand-navy">
              Entenderla ayuda a la ciudadanía a seguir el proceso con criterio
            </strong>
            , más allá del debate político del día.
          </p>
        </section>

        {/* Ficha del autor */}
        <section className="mt-10 text-sm text-slate-700">
          <p>
            Por <strong className="text-brand-navy">Jaime Freundt — JFT</strong>. Experto en Temas
            Electorales · Consultor · Asesor Político.
          </p>
        </section>

        {/* Aviso de neutralidad */}
        <aside className="mt-10 rounded-lg border border-slate-300 bg-slate-50 p-5 text-sm text-slate-700 leading-relaxed">
          <p>
            <strong className="text-brand-navy">VERDADELECTORAL.COM</strong> ofrece análisis
            técnico-electoral de interés público. Este documento{" "}
            <strong>expone las distintas interpretaciones jurídicas en juego</strong> y{" "}
            <em>no constituye patrocinio ni defensa</em> de ninguna candidatura, organización
            política ni persona.
          </p>
        </aside>

        {/* Volver */}
        <div className="mt-10">
          <Link to="/erm2026" className="text-sm text-brand-red hover:underline font-semibold">
            ← Volver a ERM 2026
          </Link>
        </div>
      </main>
    </div>
  );
}
